import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { X, Calendar as CalendarIcon, Loader2, ExternalLink } from "lucide-react";
import { ApprovalHistory } from "../../../../components/notificationv2";
import { getTimeoffRequestDetails } from "../../../../redux/features/timeoff/api";
import { RootState, useAppDispatch } from "../../../../redux/store";
import type { TimeoffRequest } from "../../../../types/model/timeoff";
import { SchedulingToolUtils } from "../../../../utils/schedulingToolConfig";
import { DateTimeUtil } from "../../../../utils/dateTime";

export interface TimeoffDetailModalProps {
    open: boolean;
    onClose: () => void;
    timeOffRequestId: string | null;
    /** schedules_id from the schedule timeoff (used for withdraw → deleteTimesheet). If null, Withdraw is hidden. */
    schedulesId?: string | null;
    onWithdraw?: (params: { schedules_id: string; notes?: string }) => void;
    isWithdrawing?: boolean;
    user?: any;
}

/** True if current user is one of the approval actors in workflow_details (actor_json). */
function isApprovalActorInWorkflow(user: any, workflowDetails: any[] | undefined): boolean {
    if (!user || !workflowDetails?.length) return false;
    const userIds = [
        user.id,
        user.userAccountId,
        user.os_emp_account_id,
        user.employee_id,
    ]
        .filter(Boolean)
        .map(String);
    if (userIds.length === 0) return false;
    return workflowDetails.some((step) => {
        const actors = Object.values(step.actor_json || {});
        return actors.some((actor: any) => {
            const actorId = actor?.employee_id ?? actor?.id ?? actor?.emp_account_id;
            return actorId != null && userIds.includes(String(actorId));
        });
    });
}

const TimeoffDetailModal: React.FC<TimeoffDetailModalProps> = ({
    open,
    onClose,
    timeOffRequestId,
    schedulesId,
    onWithdraw,
    isWithdrawing = false,
    user,
}) => {
    const dispatch = useAppDispatch();
    const { timeoffDetails, isLoadingTimeoffDetails } = useSelector((state: RootState) => state.timeoff);
    const [showWithdrawNotes, setShowWithdrawNotes] = React.useState(false);
    const [withdrawNotes, setWithdrawNotes] = React.useState("");

    useEffect(() => {
        if (!open || !timeOffRequestId || String(timeOffRequestId).trim() === "") {
            return;
        }
        dispatch(
            getTimeoffRequestDetails({
                time_off_request_id: String(timeOffRequestId).trim(),
            })
        );
    }, [open, timeOffRequestId, dispatch]);

    useEffect(() => {
        if (!open) {
            setShowWithdrawNotes(false);
            setWithdrawNotes("");
        }
    }, [open]);

    if (!open) return null;

    const details = timeoffDetails as TimeoffRequest | undefined;
    const isLoading = isLoadingTimeoffDetails && !details;
    const isCurrentRequest = details?.time_off_request_id != null && String(details.time_off_request_id) === String(timeOffRequestId);
    const showDetails = details && isCurrentRequest && !isLoading;

    const statusUpper = details?.status?.toUpperCase() ?? "";
    const isApproved = statusUpper === "APPROVED";
    const isSuperAdmin = user ? SchedulingToolUtils.isSuperAdmin(user) : false;
    const isApprovalActor = isApprovalActorInWorkflow(user, details?.workflow_details);
    const isRequestor =
        details?.requestor?.employee_id != null &&
        user?.os_emp_account_id != null &&
        String(details.requestor.employee_id) === String(user.os_emp_account_id);
    // Withdraw only for non–past dates (use request start_date, same idea as unavailability)
    const startDateStr = details?.start_date;
    const notPastDate = (() => {
        if (!startDateStr || typeof startDateStr !== "string") return true;
        const parts = startDateStr.split("-").map(Number);
        if (parts.length < 3) return true;
        const [y, m, d] = parts;
        const requestStartDate = new Date(y, m - 1, d);
        return !SchedulingToolUtils.isPastDate(requestStartDate);
    })();
    const canShowWithdrawButton =
        isApproved &&
        notPastDate &&
        (isSuperAdmin || isApprovalActor || isRequestor) &&
        schedulesId != null &&
        String(schedulesId).trim() !== "";

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1500]">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] m-4 flex flex-col">
                {/* Header: Title + Status Badge */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                            <CalendarIcon className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">Time Off Details</h3>
                            <p className="text-sm text-gray-500">
                                {showDetails && details?.requestor?.name ? details.requestor.name : "—"}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {showDetails && details && (
                            <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    statusUpper === "APPROVED"
                                        ? "bg-green-100 text-green-800"
                                        : statusUpper === "REJECTED"
                                          ? "bg-red-100 text-red-800"
                                          : "bg-yellow-100 text-yellow-800"
                                }`}
                            >
                                {details.workflow_current_step_details?.status_text ?? details.status ?? "—"}
                            </span>
                        )}
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                            <X size={24} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    {isLoading && (
                        <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
                            <Loader2 className="w-6 h-6 animate-spin flex-shrink-0" />
                            <span>Loading details...</span>
                        </div>
                    )}

                    {!isLoading && !showDetails && timeOffRequestId && (
                        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                            <p className="text-sm text-amber-800">
                                {details ? "Details could not be loaded." : "Failed to load request details."}
                            </p>
                        </div>
                    )}

                    {showDetails && details && (
                        <>
                            {/* Request Info: Type, Notes */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-2">Request Info</h4>
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
                                    <div className="space-y-2 text-sm text-gray-700">
                                        {details.category?.category_name && (
                                            <div>
                                                <span className="font-medium">Type of leave:</span>{" "}
                                                {details.category.category_name}
                                                {/* {details.policy_name ? ` (${details.policy_name})` : ""} */}
                                            </div>
                                        )}
                                        {!details.category?.category_name && details.policy_name && (
                                            <div>
                                                <span className="font-medium">Type:</span> {details.policy_name}
                                            </div>
                                        )}
                                        {details.earning_info?.earnings_code_name && (
                                            <div>
                                                <span className="font-medium">Earning code:</span>{" "}
                                                {details.earning_info.earnings_code_name}
                                            </div>
                                        )}
                                        <div>
                                            <span className="font-medium">Amount requested:</span>{" "}
                                            {details.amount_requested}
                                            {details.category?.track_time_in &&
                                            String(details.category.track_time_in).toLowerCase() === "days"
                                                ? " days"
                                                : " hours"}
                                        </div>
                                        {(details.start_date || details.end_date) && (
                                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                                                {details.start_date && (
                                                    <span>
                                                        <span className="font-medium">Start:</span>{" "}
                                                        {DateTimeUtil.formatShortDate(details.start_date)}
                                                    </span>
                                                )}
                                                {details.end_date && (
                                                    <span>
                                                        <span className="font-medium">End:</span>{" "}
                                                        {DateTimeUtil.formatShortDate(details.end_date)}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {details.notes && String(details.notes).trim() !== "" && (
                                        <div className="pt-2 border-t border-gray-200">
                                            <span className="font-medium text-gray-700">Notes:</span>
                                            <p className="text-gray-700 mt-1">{details.notes}</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Approval Progress (Approval History) */}
                            {details.workflow_details && details.workflow_details.length > 0 && (
                                <div>
                                    <ApprovalHistory
                                        workflowDetails={details.workflow_details}
                                        title="Approval Progress"
                                        showTitle={true}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>

                {showWithdrawNotes && showDetails && schedulesId && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[1501]">
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-md m-4 p-6">
                            <h4 className="text-base font-semibold text-gray-900 mb-2">Withdraw time off request</h4>
                            <p className="text-sm text-gray-600 mb-3">Add a note (optional):</p>
                            <textarea
                                value={withdrawNotes}
                                onChange={(e) => setWithdrawNotes(e.target.value)}
                                placeholder="e.g. Withdrawn by manager"
                                disabled={isWithdrawing}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60 disabled:bg-gray-50"
                                rows={3}
                            />
                            <div className="flex justify-end gap-2 mt-4">
                                <button
                                    type="button"
                                    disabled={isWithdrawing}
                                    onClick={() => {
                                        if (isWithdrawing) return;
                                        setShowWithdrawNotes(false);
                                        setWithdrawNotes("");
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={isWithdrawing}
                                    onClick={() => {
                                        if (isWithdrawing) return;
                                        onWithdraw?.({
                                            schedules_id: String(schedulesId).trim(),
                                            notes: withdrawNotes.trim() || undefined,
                                        });
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-blue-600 bg-transparent border border-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 min-w-[140px]"
                                >
                                    {isWithdrawing ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                                            Withdrawing...
                                        </>
                                    ) : (
                                        "Confirm Withdraw"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Action: buttons */}
                <div className="flex justify-between items-center gap-3 p-6 border-t border-gray-200 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        {/* Review request: when notification_transaction_id is present and user is available */}
                        {showDetails && details && details.notification_transaction_id != null && String(details.notification_transaction_id).trim() !== "" && user?.os_emp_account_id != null && (
                            <button
                                type="button"
                                onClick={() => {
                                    const transactionId = String(details!.notification_transaction_id!).trim();
                                    const employeeId = Number(user!.os_emp_account_id);
                                    try {
                                        localStorage.setItem(
                                            "notification_detail_pending",
                                            JSON.stringify({
                                                category: "TIMEOFF_APPROVAL",
                                                transactionId,
                                                employeeId,
                                            })
                                        );
                                    } catch (_) {}
                                    onClose();
                                    window.open("/notificationDetail", "_blank", "noopener,noreferrer");
                                }}
                                className="px-4 py-2 text-sm font-medium text-blue-600 bg-transparent border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors inline-flex items-center gap-2"
                            >
                                <ExternalLink className="w-4 h-4 flex-shrink-0" />
                                Review request
                            </button>
                        )}
                        {showDetails && canShowWithdrawButton && !showWithdrawNotes && (
                            <button
                                type="button"
                                onClick={() => setShowWithdrawNotes(true)}
                                className="px-4 py-2 text-sm font-medium text-red-700 bg-transparent border-2 border-red-500 rounded-lg hover:bg-red-50 transition-colors"
                            >
                                Withdraw Request
                            </button>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors ml-auto"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TimeoffDetailModal;
